import { Button } from "@climatepartner/components/Button";
import { isolatePhrase, renderVariable } from "@climatepartner/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import CheckIcon from "@mui/icons-material/Check";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ReactElement, ReactNode, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Link as RouterLink } from "react-router-dom";
import { z } from "zod";

import { countryList } from "../../../../../shared/lib/countries";
import { buildNetworkSupplierForwardInvitePath } from "../../../../../shared/routing/paths/network";
import { submitNewsletterSignup } from "../api/newsletter-signup";
import { useCreateAccount } from "../api/use-create-account";
import { useGetIndustries } from "../api/use-get-industries";

export interface AccountCreationFormProps {
  token: string;
  buyerName: string;
  requestDueDate: string;
  connectionRequestId: string;
}

const accountCreationSchema = z.object({
  name: z.string().trim().min(1, "Contact name is required"),
  email: z.string().trim().min(1, "Email is required").email("Invalid email address"),
  company: z.string().trim().min(1, "Company is required"),
  country: z.string().trim().min(1, "Country is required"),
  phone: z.string().trim(),
  // Option-set value carried as a string so the Select has an empty state; numeric on submit.
  industry: z.string(),
  terms: z.literal(true),
  newsletter: z.boolean(),
});

type AccountCreationFormValues = z.infer<typeof accountCreationSchema>;

const cardSx = {
  borderRadius: 2,
  border: 1,
  borderColor: "divider",
  width: "100%",
} as const;

// Same legal pages the buyer's invitation email links to (email-template.mjml).
const TERMS_URL = "https://www.climatepartner.com/en/terms-and-conditions";
const PRIVACY_URL = "https://www.climatepartner.com/en/privacy";

const TERMS_CONSENT_LABEL_ID = "terms-consent-label";

// New tab, so a half-filled form survives the reader following a link.
const LegalLink = ({ href, children }: { href: string; children: ReactNode }): ReactElement => (
  <Link href={href} target="_blank" rel="noopener" color="inherit">
    {children}
  </Link>
);

export const InvitationCard = ({
  buyerName,
  requestDueDate,
  forwardInviteHref,
}: {
  buyerName: string;
  requestDueDate: string;
  forwardInviteHref?: string;
}): ReactElement => (
  <Box sx={{ ...cardSx, border: 0, bgcolor: "background.paper", px: 2.5, py: 2.5 }}>
    <Typography
      variant="caption"
      // No DS token for 0.8px letter-spacing (caption default is ~0.4px); raise to design.
      sx={{ display: "block", textTransform: "uppercase", fontWeight: 600, letterSpacing: "0.8px", mb: 1 }}
    >
      Invitation
    </Typography>
    <Typography variant="h4">
      {isolatePhrase(
        <>
          <Box component="span" sx={{ fontWeight: 700 }}>
            {renderVariable(buyerName)}
          </Box>{" "}
          is requesting your Product Carbon Footprint (PCF) data.
        </>
      )}
    </Typography>
    <Typography variant="body1" sx={{ mt: 1, color: "text.secondary" }}>
      {isolatePhrase(
        <>
          Respond by{" "}
          <Box component="span" sx={{ fontWeight: 500, color: "text.primary" }}>
            {renderVariable(requestDueDate)}
          </Box>
        </>
      )}
    </Typography>
    {forwardInviteHref && (
      <Typography variant="caption" sx={{ display: "block", mt: 1.5, color: "text.secondary" }}>
        {isolatePhrase(
          <>
            Check with your team first, a colleague may already have access to ClimatePartner for your company.{" "}
            <Link component={RouterLink} to={forwardInviteHref} color="inherit" sx={{ fontWeight: 700 }}>
              Ask the colleague to invite you from their account, or instead forward the email request to the colleague
              who already has access.
            </Link>
          </>
        )}
      </Typography>
    )}
  </Box>
);

const ConfirmationCard = (): ReactElement => (
  <Box sx={{ ...cardSx, bgcolor: "background.paper", px: 4, py: 6, textAlign: "center" }}>
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: "50%",
        bgcolor: "primary.light",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        mb: 2,
      }}
    >
      <CheckIcon sx={{ color: "text.primary" }} />
    </Box>
    <Typography variant="h4" sx={{ mb: 1 }}>
      Congratulations!
    </Typography>
    <Typography variant="body2" sx={{ color: "text.secondary" }}>
      Your verification email is on its way. Activate your account to start your journey on ClimatePartner.
    </Typography>
  </Box>
);

export const AccountCreationForm = ({
  token,
  buyerName,
  requestDueDate,
  connectionRequestId,
}: AccountCreationFormProps): ReactElement => {
  const [submitted, setSubmitted] = useState(false);
  const { mutate, isPending, error } = useCreateAccount();
  const { data: industries, isLoading: isLoadingIndustries } = useGetIndustries();

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<AccountCreationFormValues>({
    resolver: zodResolver(accountCreationSchema),
    defaultValues: {
      name: "",
      email: "",
      company: "",
      country: "",
      phone: "",
      industry: "",
      terms: false as unknown as true,
      newsletter: false,
    },
  });

  const termsAccepted = watch("terms");

  const onSubmit = async (values: AccountCreationFormValues): Promise<void> => {
    try {
      await mutate({
        connectionRequestId,
        token,
        name: values.name,
        email: values.email,
        company: values.company,
        country: values.country,
        phone: values.phone || undefined,
        industry: values.industry ? Number(values.industry) : undefined,
      });
      if (values.newsletter) void submitNewsletterSignup(values.email);
      setSubmitted(true);
    } catch {
      // Error surfaced via the hook's typed `error`; nothing else to do here.
    }
  };

  if (submitted) {
    return (
      <Stack sx={{ maxWidth: 560, width: "100%", mx: "auto" }}>
        <ConfirmationCard />
      </Stack>
    );
  }

  return (
    <Stack spacing={2} sx={{ maxWidth: 560, width: "100%", mx: "auto" }}>
      <InvitationCard
        buyerName={buyerName}
        requestDueDate={requestDueDate}
        forwardInviteHref={buildNetworkSupplierForwardInvitePath(connectionRequestId, token)}
      />

      <Box sx={{ ...cardSx, bgcolor: "background.paper", px: 2.5, py: 3 }}>
        <Typography variant="h4" sx={{ mb: 2 }}>
          Create your account to respond to this request
        </Typography>

        <Stack component="form" spacing={2} onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Contact name"
                required
                fullWidth
                size="small"
                error={Boolean(errors.name)}
                helperText={errors.name?.message}
              />
            )}
          />
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Email"
                required
                fullWidth
                size="small"
                error={Boolean(errors.email)}
                helperText={errors.email?.message}
              />
            )}
          />
          <Controller
            name="company"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Company"
                required
                fullWidth
                size="small"
                error={Boolean(errors.company)}
                helperText={errors.company?.message}
              />
            )}
          />
          <Controller
            name="country"
            control={control}
            render={({ field }) => (
              <FormControl size="small" fullWidth error={Boolean(errors.country)}>
                <InputLabel id="country-label" required>
                  Country
                </InputLabel>
                <Select {...field} labelId="country-label" label="Country">
                  {countryList.map((country) => (
                    <MenuItem key={country.isoCode} value={country.isoCode}>
                      {country.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
          <Stack direction="row" spacing={2}>
            <Controller
              name="phone"
              control={control}
              render={({ field }) => <TextField {...field} label="Phone" fullWidth size="small" />}
            />
            <Controller
              name="industry"
              control={control}
              render={({ field }) => (
                <FormControl size="small" fullWidth disabled={isLoadingIndustries}>
                  <InputLabel id="industry-label">Industry</InputLabel>
                  <Select {...field} labelId="industry-label" label="Industry">
                    {(industries ?? []).map((industry) => (
                      <MenuItem key={industry.value} value={String(industry.value)}>
                        {industry.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Stack>
          <Controller
            name="terms"
            control={control}
            render={({ field }) => (
              // Not a FormControlLabel: a <label> natively forwards clicks to its control, and the
              // links inside this copy cannot cancel that (the native event reaches the label before
              // React's handler runs). Labelled by id instead, so the visible text is still the name.
              <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start", pl: 1.5 }}>
                <Checkbox
                  name={field.name}
                  checked={field.value}
                  onChange={(event) => field.onChange(event.target.checked)}
                  onBlur={field.onBlur}
                  size="small"
                  sx={{ p: 0, mt: 0.25 }}
                  slotProps={{ input: { "aria-labelledby": TERMS_CONSENT_LABEL_ID } }}
                />
                <Typography id={TERMS_CONSENT_LABEL_ID} variant="caption" sx={{ color: "text.secondary" }}>
                  {isolatePhrase(
                    <>
                      I have read and agree to ClimatePartner and CPHub&apos;s{" "}
                      <LegalLink href={TERMS_URL}>Terms and Conditions</LegalLink> /{" "}
                      <LegalLink href={PRIVACY_URL}>Privacy Policy</LegalLink>
                    </>
                  )}
                </Typography>
              </Stack>
            )}
          />
          <Controller
            name="newsletter"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    name={field.name}
                    checked={field.value}
                    onChange={(event) => field.onChange(event.target.checked)}
                    onBlur={field.onBlur}
                    size="small"
                  />
                }
                label={
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    Yes, I&apos;d like to receive news, product updates, and sustainability insights from ClimatePartner
                    by email. You can unsubscribe or opt out at any time.
                  </Typography>
                }
              />
            )}
          />

          {error === 403 && (
            <Typography color="error" variant="body2">
              The email address you entered doesn&apos;t match this invitation.
            </Typography>
          )}
          {error === 409 && (
            <Typography color="error" variant="body2">
              This invitation has already been used to register. If you already have an account, please log in instead.
            </Typography>
          )}
          {error === 400 && (
            <Typography color="error" variant="body2">
              Please check the highlighted fields and try again.
            </Typography>
          )}
          {error === 503 && (
            <Typography color="error" variant="body2">
              Something went wrong. Please try again.
            </Typography>
          )}

          <Button
            onClick={handleSubmit(onSubmit)}
            fullWidth
            disabled={!termsAccepted || isPending}
            isLoading={isPending}
          >
            Continue →
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
};
